import React, { useEffect, useMemo, useState } from "react";
import Navbar from "./components/Navbar";
import Popup from "./components/Popup";
import BcModal from "./components/BcModal";
import CertificatesModal from "./components/CertificatesModal";
import { defaultSiteConfig, mergeSiteConfig } from "./siteConfig";
import HomePage from "./pages/HomePage";
import CatalogPage from "./pages/CatalogPage";
import CoursePage from "./pages/CoursePage";
import DashboardPage from "./pages/DashboardPage";
import LoginPage from "./pages/LoginPage";
import CoursesPage from "./pages/CoursesPage";
import AdminPage from "./pages/AdminPage";
import UtilsPage from "./pages/UtilsPage";
import {
  AuthResponse,
  AuthUser,
  CourseModule,
  HomeBanner,
  ProductItem,
  getApiHealth,
  getCourseModules,
  getHomeBanners,
  getOnlineUsers,
  isLocalSessionToken,
  markLessonWatched,
  getProducts,
  getSiteConfig,
  issueCertificate,
  getPublicStats,
  getUserStats,
  loginByDocument,
  loginDemo,
  loginGuest,
} from "./services/api";
import type {
  CourseItem,
  CourseProgressSummary,
  DashboardStats,
  PageKey,
  PublicMetrics,
  SelectedLessonState,
} from "./appTypes";
import "./App.css";

const SESSION_STORAGE_KEY = "blueacademy.session";
const MEMBERS_TOKEN_KEY = "bulls:token";
const WATCHED_LESSONS_KEY = "blueacademy.watchedLessons";

const defaultMetrics: PublicMetrics = {
  totalStudents: 0,
  activeStudents: 0,
  onlineUsers: 0,
};

const defaultDashboardStats: DashboardStats = {
  completionPercent: 0,
  watchedLessons: 0,
  totalLessons: 0,
  toolsPercent: 0,
  ownedProducts: 0,
  totalProducts: 0,
};

function loadWatchedLessons() {
  try {
    const raw = window.localStorage.getItem(WATCHED_LESSONS_KEY);
    if (!raw) return [] as number[];
    const parsed = JSON.parse(raw) as number[];
    return Array.isArray(parsed)
      ? parsed.filter((value) => Number.isFinite(value))
      : [];
  } catch {
    return [] as number[];
  }
}

function mapModulesToCourses(modules: CourseModule[]): CourseItem[] {
  return modules.flatMap((module) =>
    module.lessons.map((lesson) => ({
      id: lesson.id,
      title: lesson.title,
      thumbnail:
        lesson.thumbnail_url ||
        module.cover_url ||
        "https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?auto=format&fit=crop&w=900&q=80",
      subtitle: module.title,
    })),
  );
}

function getProductActionLabel(product: ProductItem) {
  if (product.hasAccess)
    return product.isCourse ? "Abrir curso" : "Acessar agora";
  if (product.requiresExternalSignup) return "Fazer cadastro";
  if (product.checkoutUrl) return "Comprar acesso";
  return "Ver detalhes";
}

function getEmbedVideoUrl(url: string) {
  if (!url) return "";
  if (url.includes("player.vimeo.com")) return url;

  const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
  if (!vimeoMatch) return url;

  return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
}

function getPageFromHash(hash: string): PageKey {
  const normalized = hash.replace(/^#\/?/, "").trim().toLowerCase();
  if (!normalized) return "login";
  if (normalized === "admin" || normalized.startsWith("admin/")) return "admin";
  if (normalized === "curso" || normalized.startsWith("curso/")) return "curso";
  if (normalized === "login") return "login";
  if (normalized === "catalogo") return "catalogo";
  if (normalized === "dashboard") return "home";
  if (normalized === "ferramentas") return "home";
  if (normalized === "utilidades") return "utilidades";
  return "home";
}

function getCourseIdFromHash(hash: string) {
  const normalized = hash.replace(/^#\/?/, "").trim();
  const [page, courseId] = normalized.split("/");
  if (page?.toLowerCase() !== "curso") return null;
  return courseId?.trim() || null;
}

const App: React.FC = () => {
  const [showPopup, setShowPopup] = useState(false);
  const [welcomeOpen, setWelcomeOpen] = useState(false);
  const [unlockOpen, setUnlockOpen] = useState(false);
  const [certificatesOpen, setCertificatesOpen] = useState(false);
  const [bootChecked, setBootChecked] = useState(false);
  const [apiStatus, setApiStatus] = useState<"checking" | "online" | "offline">(
    "checking",
  );
  const [databaseStatus, setDatabaseStatus] = useState("desconhecido");
  const [documentInput, setDocumentInput] = useState("");
  const [session, setSession] = useState<AuthResponse | null>(null);
  const [siteConfig, setSiteConfig] = useState(defaultSiteConfig);
  const [products, setProducts] = useState<ProductItem[]>([]);
  const [homeBanners, setHomeBanners] = useState<HomeBanner[]>([]);
  const [metrics, setMetrics] = useState<PublicMetrics>(defaultMetrics);
  const [dashboardStats, setDashboardStats] = useState<DashboardStats>(
    defaultDashboardStats,
  );
  const [courseModules, setCourseModules] = useState<
    Record<string, CourseModule[]>
  >({});
  const [selectedLesson, setSelectedLesson] =
    useState<SelectedLessonState | null>(null);
  const [watchedLessonIds, setWatchedLessonIds] =
    useState<number[]>(loadWatchedLessons);
  const [currentPage, setCurrentPage] = useState<PageKey>(() =>
    getPageFromHash(window.location.hash),
  );
  const [routeHash, setRouteHash] = useState<string>(
    () => window.location.hash,
  );
  const [loadingAuth, setLoadingAuth] = useState(false);
  const [authTransitionActive, setAuthTransitionActive] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [authError, setAuthError] = useState("");
  const [pageError, setPageError] = useState("");

  useEffect(() => {
    const stored = window.localStorage.getItem(SESSION_STORAGE_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored) as AuthResponse;
        if (parsed?.token && parsed?.user) {
          setSession(parsed);
        }
      } catch {
        window.localStorage.removeItem(SESSION_STORAGE_KEY);
      }
    }
    setBootChecked(true);
  }, []);

  // Popup de boas-vindas (1x por sessão de navegação, ao entrar logado).
  useEffect(() => {
    if (!session?.token || !siteConfig.welcomePopup.enabled) return;
    try {
      if (sessionStorage.getItem("bc:welcome") === "1") return;
      sessionStorage.setItem("bc:welcome", "1");
    } catch { /* ignore */ }
    setWelcomeOpen(true);
  }, [session]);

  function persistSession(nextSession: AuthResponse) {
    setSession(nextSession);
    window.localStorage.setItem(
      SESSION_STORAGE_KEY,
      JSON.stringify(nextSession),
    );
    window.localStorage.setItem(MEMBERS_TOKEN_KEY, nextSession.token);
  }

  useEffect(() => {
    const handleHashChange = () => {
      if (window.location.hash.replace(/^#\/?/, "").trim().toLowerCase() === "dashboard") {
        window.location.hash = "/home";
        return;
      }

      setCurrentPage(getPageFromHash(window.location.hash));
      setRouteHash(window.location.hash);
      window.scrollTo({ top: 0, behavior: "smooth" });
    };

    window.addEventListener("hashchange", handleHashChange);
    return () => window.removeEventListener("hashchange", handleHashChange);
  }, []);

  useEffect(() => {
    window.localStorage.setItem(
      WATCHED_LESSONS_KEY,
      JSON.stringify(watchedLessonIds),
    );
  }, [watchedLessonIds]);

  useEffect(() => {
    if (!window.location.hash) {
      window.location.hash = "/login";
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();

    getApiHealth(controller.signal)
      .then((data) => {
        setApiStatus(data.status === "ok" ? "online" : "offline");
        setDatabaseStatus(data.database || "desconhecido");
      })
      .catch(() => {
        setApiStatus("offline");
        setDatabaseStatus("indisponivel");
      });

    return () => controller.abort();
  }, []);

  // Configurações editadas no admin (links, popups, agenda da live, avisos do
  // sininho). Vêm de GET /api/site-config; sem a rota, mantém defaultSiteConfig.
  useEffect(() => {
    let cancelled = false;
    getSiteConfig(session?.token)
      .then((remote) => {
        if (!cancelled) setSiteConfig(mergeSiteConfig(remote));
      })
      .catch(() => {
        /* backend ainda não expõe /api/site-config → usa os defaults */
      });
    return () => {
      cancelled = true;
    };
  }, [session?.token]);

  useEffect(() => {
    let cancelled = false;

    async function loadAppData() {
      setLoadingData(true);
      setPageError("");

      try {
        const [publicStats, onlineStats, homeBannerResponse] = await Promise.all([
          getPublicStats(),
          getOnlineUsers(),
          getHomeBanners().catch(() => ({ banners: [] })),
        ]);

        if (!cancelled) {
          setMetrics({
            totalStudents: publicStats.totalStudents,
            activeStudents: publicStats.activeStudents,
            onlineUsers: onlineStats.onlineUsers,
          });
          setHomeBanners(homeBannerResponse.banners);
        }

        if (!session?.token) {
          if (!cancelled) {
            setProducts([]);
            setCourseModules({});
            setDashboardStats(defaultDashboardStats);
            setSelectedLesson(null);
          }
          return;
        }

        const productsResponse = await getProducts(session.token);
        const courseProducts = productsResponse.products.filter(
          (product) => product.isCourse,
        );
        const modulesResults = await Promise.allSettled(
          courseProducts.map(async (course) => {
            const modulesResponse = await getCourseModules(
              course.id,
              session.token,
            );
            return [
              course.id,
              {
                modules: modulesResponse.modules,
                watchedLessonIds: modulesResponse.watchedLessonIds || [],
              },
            ] as const;
          }),
        );

        if (!cancelled) {
          const modulesEntries = modulesResults
            .filter(
              (
                result,
              ): result is PromiseFulfilledResult<
                readonly [
                  string,
                  {
                    modules: CourseModule[];
                    watchedLessonIds: number[];
                  },
                ]
              > => result.status === "fulfilled",
            )
            .map((result) => result.value);
          const nextModules = Object.fromEntries(
            modulesEntries.map(([courseId, value]) => [
              courseId,
              value.modules,
            ]),
          );
          const watchedFromApi = modulesEntries.flatMap(
            ([, value]) => value.watchedLessonIds,
          );
          setProducts(productsResponse.products);
          setCourseModules(nextModules);
          setWatchedLessonIds((current) =>
            Array.from(new Set([...current, ...watchedFromApi])),
          );

          if (!selectedLesson) {
            const firstCourse = courseProducts[0];
            const firstModule = firstCourse
              ? nextModules[firstCourse.id]?.[0]
              : null;
            const firstLesson = firstModule?.lessons?.[0];

            if (firstCourse && firstModule && firstLesson) {
              setSelectedLesson({
                courseId: firstCourse.id,
                courseTitle: firstCourse.title,
                moduleTitle: firstModule.title,
                lesson: firstLesson,
              });
            }
          }
        }

        const canLoadUserStats = Number.isFinite(Number(session.user.id));
        if (canLoadUserStats) {
          try {
            const userStats = await getUserStats(session.token);
            if (!cancelled) {
              setDashboardStats(userStats);
            }
          } catch {
            if (!cancelled) {
              setDashboardStats(defaultDashboardStats);
            }
          }
        } else if (!cancelled) {
          setDashboardStats(defaultDashboardStats);
        }
      } catch (error) {
        if (!cancelled) {
          if (session?.token && isLocalSessionToken(session.token)) {
            setProducts([]);
            setCourseModules({});
            setDashboardStats(defaultDashboardStats);
            setSelectedLesson(null);
            setPageError("");
          } else {
            setPageError(
              error instanceof Error ? error.message : "Erro ao carregar dados",
            );
          }
        }
      } finally {
        if (!cancelled) {
          setLoadingData(false);
        }
      }
    }

    loadAppData();

    return () => {
      cancelled = true;
    };
  }, [session]);

  const liveProduct = useMemo(
    () => products.find((product) => product.id === "vip") || null,
    [products],
  );

  const accessibleCourses = useMemo(
    () => products.filter((product) => product.isCourse && product.hasAccess),
    [products],
  );

  const planilhaProduct = useMemo(
    () =>
      products.find((product) => {
        const targets = [
          product.accessUrl,
          product.productUrl,
          product.checkoutUrl,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return targets.includes("/acesso/planilha");
      }) || null,
    [products],
  );

  const overviewCards = useMemo(
    () => [
      {
        label: "Progresso das aulas",
        value: `${dashboardStats.completionPercent}%`,
        helper: `${dashboardStats.watchedLessons} de ${dashboardStats.totalLessons} aulas assistidas`,
      },
      {
        label: "Ferramentas liberadas",
        value: `${dashboardStats.toolsPercent}%`,
        helper: `${dashboardStats.ownedProducts} de ${dashboardStats.totalProducts} acessos ativos`,
      },
      {
        label: "Pessoas online agora",
        value: `${metrics.onlineUsers}`,
        helper: "contador sincronizado pela API",
      },
    ],
    [dashboardStats, metrics.onlineUsers],
  );

  async function handleAuth(
    action: () => Promise<AuthResponse>,
    options?: { animate?: boolean },
  ) {
    setLoadingAuth(true);
    setAuthError("");

    try {
      const nextSession = await action();
      const shouldAnimate = options?.animate ?? true;

      if (shouldAnimate) {
        setAuthTransitionActive(true);
        window.setTimeout(() => {
          persistSession(nextSession);
          window.location.hash = "/home";
          window.setTimeout(() => {
            setAuthTransitionActive(false);
          }, 260);
        }, 180);
      } else {
        persistSession(nextSession);
        window.location.hash = "/home";
      }
    } catch (error) {
      setAuthError(
        error instanceof Error ? error.message : "Não foi possível autenticar",
      );
    } finally {
      setLoadingAuth(false);
    }
  }

  function logout() {
    setSession(null);
    setProducts([]);
    setCourseModules({});
    setDashboardStats(defaultDashboardStats);
    setSelectedLesson(null);
    window.localStorage.removeItem(SESSION_STORAGE_KEY);
    window.localStorage.removeItem(MEMBERS_TOKEN_KEY);
    try { window.sessionStorage.removeItem("bc:welcome"); } catch { /* ignore */ }
    window.location.hash = "/login";
  }

  function handleSelectLesson(
    courseId: string,
    courseTitle: string,
    lessonId: number,
  ) {
    const modules = courseModules[courseId] || [];

    for (const module of modules) {
      const lesson = module.lessons.find((item) => item.id === lessonId);
      if (lesson) {
        setSelectedLesson({
          courseId,
          courseTitle,
          moduleTitle: module.title,
          lesson,
        });
        try {
          window.localStorage.setItem(
            "bc:lastLesson",
            JSON.stringify({
              courseId,
              courseTitle,
              moduleTitle: module.title,
              lessonId: lesson.id,
              lessonTitle: lesson.title,
              cover: lesson.thumbnail_url || module.cover_url || "",
              ts: Date.now(),
            }),
          );
        } catch {
          /* ignore */
        }
        setWatchedLessonIds((current) =>
          current.includes(lesson.id) ? current : [...current, lesson.id],
        );

        if (session?.token) {
          markLessonWatched(lesson.id, session.token).catch(() => {
            // Mantemos o progresso local se o backend não conseguir persistir.
          });
        }

        window.location.hash = `/curso/${courseId}`;
        break;
      }
    }
  }

  function openCourseById(courseId: string) {
    if (!courseId) return;

    const course = accessibleCourses.find((item) => item.id === courseId);
    const modules = courseModules[courseId] || [];
    const firstLesson = modules[0]?.lessons?.[0];

    if (course && firstLesson) {
      handleSelectLesson(course.id, course.title, firstLesson.id);
      return;
    }

    window.location.hash = `/curso/${courseId}`;
  }

  async function handleIssueCertificate(programName: string) {
    if (!session?.token || isLocalSessionToken(session.token)) return;
    await issueCertificate(session.token, {
      programName,
      progressPercent: overallProgress,
    });
  }

  useEffect(() => {
    if (!bootChecked) return;
    if (!session?.token && currentPage !== "login" && currentPage !== "admin") {
      window.location.hash = "/login";
    }
  }, [currentPage, session, bootChecked]);

  const selectedCourseModules = selectedLesson
    ? courseModules[selectedLesson.courseId] || []
    : [];

  const selectedCourseLessonList = selectedCourseModules.flatMap((module) =>
    module.lessons.map((lesson) => ({
      courseId: selectedLesson?.courseId || "",
      courseTitle: selectedLesson?.courseTitle || "",
      moduleTitle: module.title,
      lesson,
    })),
  );

  const selectedLessonIndex = selectedLesson
    ? selectedCourseLessonList.findIndex(
        (item) => item.lesson.id === selectedLesson.lesson.id,
      )
    : -1;

  const previousLesson =
    selectedLessonIndex > 0
      ? selectedCourseLessonList[selectedLessonIndex - 1]
      : null;
  const nextLesson =
    selectedLessonIndex >= 0 &&
    selectedLessonIndex < selectedCourseLessonList.length - 1
      ? selectedCourseLessonList[selectedLessonIndex + 1]
      : null;

  const watchedLessonsSet = useMemo(
    () => new Set(watchedLessonIds.map((value) => Number(value))),
    [watchedLessonIds],
  );

  const courseProgressList = useMemo<CourseProgressSummary[]>(
    () =>
      accessibleCourses.map((course) => {
        const modules = courseModules[course.id] || [];
        const allLessons = modules.flatMap((module) => module.lessons);
        const watchedCount = allLessons.reduce(
          (total, lesson) => total + (watchedLessonsSet.has(lesson.id) ? 1 : 0),
          0,
        );
        const totalLessons = allLessons.length;
        const progressPercent = totalLessons
          ? Math.round((watchedCount / totalLessons) * 100)
          : 0;
        const cover =
          allLessons.find((lesson) => lesson.thumbnail_url)?.thumbnail_url ||
          modules.find((module) => module.cover_url)?.cover_url ||
          undefined;

        return {
          courseId: course.id,
          title: course.title,
          cover,
          totalLessons,
          watchedLessons: watchedCount,
          progressPercent,
          totalModules: modules.length,
        };
      }),
    [accessibleCourses, courseModules, watchedLessonsSet],
  );

  // Progresso geral (mesma base do painel de certificados e da barra da sidebar).
  const overallProgress = useMemo(() => {
    const t = courseProgressList.reduce(
      (a, c) => ({ w: a.w + c.watchedLessons, t: a.t + c.totalLessons }),
      { w: 0, t: 0 },
    );
    return t.t ? Math.round((t.w / t.t) * 100) : 0;
  }, [courseProgressList]);

  useEffect(() => {
    if (currentPage !== "curso" || loadingData || !accessibleCourses.length) return;

    const courseIdFromHash = getCourseIdFromHash(routeHash);
    if (!courseIdFromHash) return;

    const course = accessibleCourses.find((item) => item.id === courseIdFromHash);
    if (!course) return;

    if (selectedLesson?.courseId === courseIdFromHash) return;

    const modules = courseModules[courseIdFromHash] || [];
    const firstLesson = modules[0]?.lessons?.[0];
    if (!firstLesson) return;

    setSelectedLesson({
      courseId: course.id,
      courseTitle: course.title,
      moduleTitle: modules[0].title,
      lesson: firstLesson,
    });
  }, [
    accessibleCourses,
    courseModules,
    currentPage,
    loadingData,
    routeHash,
    selectedLesson?.courseId,
  ]);

  const isLoggedIn = Boolean(session?.token);
  const sessionUser: AuthUser | null = session?.user || null;
  const isLoginPage = currentPage === "login";
  const isAdminPage = currentPage === "admin";
  const isHomePage = currentPage === "home";
  const isInternalPage = isLoggedIn && !isLoginPage && !isHomePage && !isAdminPage;
  const courseIdInHash = getCourseIdFromHash(routeHash);

  return (
    <div className="app-shell">
      <div
        className={`auth-transition-overlay ${authTransitionActive ? "is-active" : ""}`}
        aria-hidden={!authTransitionActive}
      >
        <div className="auth-transition-copy">
          <span className="auth-transition-loader" aria-hidden="true" />
        </div>
      </div>
      {showPopup && (
        <Popup
          onClose={() => setShowPopup(false)}
          liveUrl={liveProduct?.accessUrl}
        />
      )}
      {!isLoginPage && !isAdminPage ? (
        <Navbar
          config={siteConfig}
          userName={sessionUser?.name}
          primaryLabel={isLoggedIn ? "Sair" : "Entrar"}
          onPrimaryAction={
            isLoggedIn ? logout : () => (window.location.hash = "/login")
          }
          currentPage={currentPage}
          isLoggedIn={isLoggedIn}
          onUnlock={() => setUnlockOpen(true)}
          onOpenCertificates={() => setCertificatesOpen(true)}
          progressPercent={overallProgress}
        />
      ) : null}

      <BcModal
        open={welcomeOpen && isLoggedIn && currentPage !== "login" && currentPage !== "admin"}
        onClose={() => setWelcomeOpen(false)}
        kicker={siteConfig.welcomePopup.kicker}
        title={siteConfig.welcomePopup.title}
        text={siteConfig.welcomePopup.text}
        ctaLabel={siteConfig.welcomePopup.ctaLabel}
        ctaUrl={siteConfig.welcomePopup.ctaUrl}
        secondaryLabel={siteConfig.welcomePopup.secondaryLabel}
        image={siteConfig.welcomePopup.image || undefined}
        icon={<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 2a10 10 0 0 0-8.6 15l-1.3 4.7 4.8-1.3A10 10 0 1 0 12 2Zm4.5 12.3c-.2-.1-1.4-.7-1.6-.8s-.4-.1-.5.1l-.7.9c-.1.2-.3.2-.5.1a6.5 6.5 0 0 1-3.2-2.8c-.2-.4.2-.4.6-1.2.1-.1 0-.3 0-.4l-.8-1.8c-.2-.5-.4-.4-.5-.4h-.5a1 1 0 0 0-.7.3 3 3 0 0 0-.9 2.2 5.2 5.2 0 0 0 1.1 2.7 11.7 11.7 0 0 0 4.5 4c2.1.8 2.1.5 2.5.5a2.6 2.6 0 0 0 1.7-1.2 2.1 2.1 0 0 0 .1-1.2c0-.1-.2-.2-.4-.3Z" /></svg>}
      />
      <BcModal
        open={unlockOpen}
        onClose={() => setUnlockOpen(false)}
        kicker={siteConfig.unlockPopup.kicker}
        title={siteConfig.unlockPopup.title}
        text={siteConfig.unlockPopup.text}
        ctaLabel={siteConfig.unlockPopup.ctaLabel}
        ctaUrl={siteConfig.unlockPopup.ctaUrl || "#/catalogo"}
        secondaryLabel={siteConfig.unlockPopup.secondaryLabel}
        icon={<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><path d="m12 3 2.5 5 5.5.8-4 3.9.9 5.5L12 21l-4.9 2.6.9-5.5-4-3.9 5.5-.8L12 3Z" /></svg>}
      />
      <CertificatesModal
        open={certificatesOpen}
        onClose={() => setCertificatesOpen(false)}
        courses={courseProgressList}
        userName={sessionUser?.name}
        onIssueCertificate={handleIssueCertificate}
      />
      {/* O banner agora é renderizado dentro da HomePage. */}

      <main
        id="page-content-start"
        className={`main-content ${isLoginPage ? "is-login" : ""} ${isInternalPage ? "is-internal" : ""}`}
      >
        {currentPage === "login" && (
          <LoginPage
            loadingAuth={loadingAuth}
            authError={authError}
            documentInput={documentInput}
            setDocumentInput={setDocumentInput}
            onLoginDemo={() => handleAuth(loginDemo, { animate: true })}
            onLoginGuest={() => handleAuth(loginGuest, { animate: true })}
            onLoginByDocument={() =>
              handleAuth(() => loginByDocument(documentInput.trim()), {
                animate: true,
              })
            }
            metrics={metrics}
            apiStatus={apiStatus}
            databaseStatus={databaseStatus}
          />
        )}

        {currentPage === "admin" && <AdminPage />}

        {currentPage === "home" && isLoggedIn && (
          <HomePage
            isLoggedIn={isLoggedIn}
            loadingAuth={loadingAuth}
            authError={authError}
            pageError={pageError}
            documentInput={documentInput}
            setDocumentInput={setDocumentInput}
            onLoginDemo={() => handleAuth(loginDemo)}
            onLoginGuest={() => handleAuth(loginGuest)}
            onLoginByDocument={() =>
              handleAuth(() => loginByDocument(documentInput.trim()))
            }
            overviewCards={overviewCards}
            userName={sessionUser?.name}
            featuredCourses={courseProgressList}
            products={products}
            onOpenCourse={openCourseById}
            accessibleProductsCount={
              products.filter((product) => product.hasAccess).length
            }
            loadingData={loadingData}
            homeBanners={homeBanners}
            liveUrl={liveProduct?.accessUrl}
            live={siteConfig.live}
            onContinue={handleSelectLesson}
          />
        )}

        {currentPage === "catalogo" && isLoggedIn && (
          <CatalogPage
            products={products}
            getProductActionLabel={getProductActionLabel}
            loadingData={loadingData}
            pageError={pageError}
          />
        )}

        {currentPage === "curso" && isLoggedIn && (courseIdInHash ? (
          <CoursePage
            accessibleCourses={accessibleCourses}
            courseModules={courseModules}
            selectedLesson={selectedLesson}
            selectedCourseModules={selectedCourseModules}
            selectedCourseLessonList={selectedCourseLessonList}
            selectedLessonIndex={selectedLessonIndex}
            previousLesson={previousLesson}
            nextLesson={nextLesson}
            watchedLessonsSet={watchedLessonsSet}
            mapModulesToCourses={mapModulesToCourses}
            handleSelectLesson={handleSelectLesson}
            getEmbedVideoUrl={getEmbedVideoUrl}
            loadingData={loadingData}
            pageError={pageError}
            userName={sessionUser?.name}
            overallProgress={overallProgress}
            onOpenCertificates={() => setCertificatesOpen(true)}
          />
        ) : (
          <CoursesPage
            courses={products.filter((product) => product.isCourse)}
            progressList={courseProgressList}
            loadingData={loadingData}
            pageError={pageError}
          />
        ))}

        {currentPage === "dashboard" && isLoggedIn && (
          <DashboardPage
            overviewCards={overviewCards}
            courseProgressList={courseProgressList}
            products={products}
            planilhaProduct={planilhaProduct}
            userName={sessionUser?.name}
            metrics={metrics}
            accessibleProductsCount={
              products.filter((product) => product.hasAccess).length
            }
            isLoggedIn={isLoggedIn}
            loadingData={loadingData}
            pageError={pageError}
          />
        )}

        {currentPage === "utilidades" && isLoggedIn && <UtilsPage />}
      </main>
    </div>
  );
};

export default App;
